export const ACTIVE_TOURNAMENT_MATCH_STATUSES = new Set([
  "check_in_open",
  "pending",
  "reported",
  "disputed",
  "resume_pending",
]);

const TERMINAL_TOURNAMENT_MATCH_STATUSES = new Set([
  "confirmed",
  "finished",
  "cancelled",
  "canceled",
  "void",
]);

function normalizeStatus(match) {
  return String(match?.status || "").toLowerCase();
}

function isOwnMatch(match, currentUid) {
  const uid = String(currentUid || "");
  return Boolean(uid) && (
    String(match?.player1Uid || "") === uid ||
    String(match?.player2Uid || "") === uid
  );
}

function hasDistinctPlayers(match) {
  const player1Uid = String(match?.player1Uid || "");
  const player2Uid = String(match?.player2Uid || "");
  return Boolean(player1Uid && player2Uid && player1Uid !== player2Uid);
}

function isGroupMatch(match) {
  return String(match?.phase || "").toLowerCase() === "group" || Boolean(match?.groupId);
}

function timestampMillis(value) {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function lifecycleVersion(match) {
  const version = Number(match?.matchLifecycleVersion ?? match?.lifecycleVersion ?? 1);
  return Number.isFinite(version) ? version : 1;
}

function activeStatusPriority(status) {
  if (status === "disputed") return 5;
  if (status === "reported") return 4;
  if (status === "resume_pending") return 3;
  if (status === "pending") return 2;
  if (status === "check_in_open") return 1;
  return 0;
}

function compareCurrentMatches(a, b) {
  const lifecycleDiff = lifecycleVersion(b) - lifecycleVersion(a);
  if (lifecycleDiff !== 0) return lifecycleDiff;

  const roundDiff = Number(b?.roundNumber || 0) - Number(a?.roundNumber || 0);
  if (roundDiff !== 0) return roundDiff;

  const legDiff = Number(b?.legNumber || 0) - Number(a?.legNumber || 0);
  if (legDiff !== 0) return legDiff;

  const statusDiff = activeStatusPriority(normalizeStatus(b)) - activeStatusPriority(normalizeStatus(a));
  if (statusDiff !== 0) return statusDiff;

  const updatedDiff = timestampMillis(b?.updatedAt || b?.createdAt) - timestampMillis(a?.updatedAt || a?.createdAt);
  if (updatedDiff !== 0) return updatedDiff;

  return String(a?.id || "").localeCompare(String(b?.id || ""));
}

export function selectCurrentTournamentMatch({
  matches = [],
  currentUid = "",
  groupStageActive = false,
} = {}) {
  const ownMatches = (Array.isArray(matches) ? matches : []).filter((match) =>
    isOwnMatch(match, currentUid) && hasDistinctPlayers(match)
  );

  // A concrete active match is authoritative for this player. Global group or
  // knockout rollout states may describe other pairings and must not hide it.
  const activeMatches = ownMatches.filter((match) =>
    ACTIVE_TOURNAMENT_MATCH_STATUSES.has(normalizeStatus(match))
  );
  if (activeMatches.length > 0) {
    return [...activeMatches].sort(compareCurrentMatches)[0];
  }

  const phaseMatches = ownMatches.filter((match) =>
    groupStageActive ? isGroupMatch(match) : !isGroupMatch(match)
  );
  if (phaseMatches.length === 0) return null;

  const latest = [...phaseMatches].sort(compareCurrentMatches)[0];
  return TERMINAL_TOURNAMENT_MATCH_STATUSES.has(normalizeStatus(latest)) ? null : latest;
}
